import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { Activity, Users, Hash, Music, Bot, Server, Plus } from "lucide-react";

export default function Overview() {
  const { active, botInviteUrl } = useServer();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    if (active?.bot_present) {
      api.get(`/bot/logs?guild_id=${active.id}&limit=10`).then((r) => setLogs(r.data));
    } else {
      setLogs([]);
    }
  }, [active?.id, active?.bot_present]);

  const cards = [
    { icon: Server, label: "Servers", value: stats?.guilds_count, color: "text-brand-orange" },
    { icon: Users, label: "Total Members", value: stats?.members_count, color: "text-emerald-400" },
    { icon: Hash, label: "Channels", value: stats?.channels_count, color: "text-blue-400" },
    { icon: Bot, label: "Commands Run", value: stats?.commands_executed, color: "text-purple-400" },
    { icon: Music, label: "Bot Status", value: stats?.bot_online ? "ONLINE" : "OFFLINE", color: stats?.bot_online ? "text-emerald-400" : "text-red-400" },
    { icon: Activity, label: "Uptime", value: stats?.uptime_days ? `${stats.uptime_days}d` : "—", color: "text-yellow-400" },
  ];

  return (
    <div data-testid="overview-panel" className="space-y-8">
      <div>
        <div className="overline mb-2">// MISSION BRIEFING</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">OVERVIEW</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">
          {active ? (
            <>Real-time telemetry from <span className="text-white font-semibold">{active.name}</span>.</>
          ) : (
            <>Select a Discord server from the sidebar to begin.</>
          )}
        </p>
      </div>

      {active && !active.bot_present && (
        <div className="tactical-card p-6 border-yellow-500/40 bg-yellow-500/5">
          <div className="overline text-yellow-400 mb-1">// ACTION REQUIRED</div>
          <h3 className="font-heading text-lg font-bold uppercase mb-2">
            Bot is not in {active.name}
          </h3>
          <p className="text-sm text-zinc-400 mb-4">
            Click "Add Bot" to install 606 Community in this server. You'll need Manage Server permission.
          </p>
          {botInviteUrl && (
            <a href={botInviteUrl} target="_blank" rel="noreferrer" className="btn-primary" data-testid="overview-add-bot-btn">
              <Plus className="w-4 h-4" /> Add Bot to Server
            </a>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="tactical-card p-5" data-testid={`overview-stat-${c.label.toLowerCase().replace(/ /g, '-')}`}>
              <Icon className={`w-5 h-5 ${c.color} mb-3`} />
              <div className="font-mono-tabular text-2xl xl:text-3xl font-bold">
                {typeof c.value === "number" ? c.value.toLocaleString() : (c.value ?? "—")}
              </div>
              <div className="overline mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 tactical-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline">// LIVE FEED</div>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mt-1">Activity Log</h2>
            </div>
            <span className="font-mono-tabular text-xs text-zinc-500">{logs.length} events</span>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto tactical-scroll" data-testid="overview-log-feed">
            {logs.length === 0 && (
              <div className="text-zinc-500 text-sm font-mono-tabular py-8 text-center">
                No events yet — start using the bot.
              </div>
            )}
            {logs.map((l) => (
              <div
                key={l.id}
                className="border border-brand-line p-3 flex items-start gap-3 hover:border-brand-orange/50 transition-colors rounded-lg"
                data-testid={`log-row-${l.id}`}
              >
                <div className={`w-1.5 h-12 rounded ${
                  l.level === "error" ? "bg-red-500" :
                  l.level === "warning" ? "bg-yellow-500" :
                  "bg-brand-orange"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="overline text-zinc-400">{l.category}</span>
                    <span className="font-mono-tabular text-[10px] text-zinc-600">
                      {new Date(l.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-sm mt-1 truncate">{l.message}</div>
                  <div className="font-mono-tabular text-[10px] text-zinc-500 mt-0.5">by {l.actor}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="tactical-card p-6">
          <div className="overline mb-1">// SYSTEM</div>
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-5">Status</h2>
          <div className="space-y-4">
            {[
              { name: "Bot Connection", ok: stats?.bot_online },
              { name: "Discord OAuth", ok: true },
              { name: "Gemini AI", ok: stats?.ai_enabled !== false },
              { name: "Database", ok: true },
              { name: "Voice Engine", ok: true },
            ].map((s) => (
              <div key={s.name} className="flex items-center justify-between border-b border-brand-line pb-3 last:border-0">
                <span className="text-sm">{s.name}</span>
                <span className={`flex items-center gap-2 text-xs font-mono-tabular uppercase ${s.ok ? "text-emerald-400" : "text-red-400"}`}>
                  <span className={`status-dot ${s.ok ? "status-online" : "status-dnd"}`} />
                  {s.ok ? "ONLINE" : "OFFLINE"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
